'use client'
import classNames from 'classnames'

import { useEffect, useState } from 'react'
import { useTranslation } from 'app/i18n/client'
import { usePathname, useRouter } from 'next/navigation'

import { Loading } from 'app/components/atoms'

import { formatToYen } from 'utils'

import ChevronLeftIcon from 'public/icons/chevron-left.svg'

interface HotelSearchBreakcrumbsProps {
  hotelName: string
  subTitle: string
  totalCost?: number
  // Undated mode: totalCost is the cheapest-of-month estimate, not a fixed
  // total, so append a "~" after the formatted amount.
  isUndated?: boolean
  onModify?: () => void
  onBack?: () => void
}

const HotelSearchBreakcrumbs: React.FunctionComponent<
  HotelSearchBreakcrumbsProps
> = ({ hotelName, subTitle, totalCost, isUndated, onModify, onBack }) => {
  const { t, i18n } = useTranslation()
  const pathname = usePathname()
  const [isBacking, setIsBacking] = useState<boolean>(false)
  const [isModifying, setIsModifying] = useState<boolean>(false)

  const router = useRouter()

  useEffect(() => {
    setIsBacking(false)
    setIsModifying(false)
  }, [pathname])

  return (
    <div className="bg-white border-t border-t-gray-150">
      <div className="px-3 lg:px-0 max-w-7xl mx-auto text-gray-light flex items-stretch">
        <div className="relative shrink-0 pr-3 lg:pr-6 border-r border-gray-150 min-w-8">
          {isBacking && (
            <div className="absolute left-0 top-0 w-full h-full z-100 flex items-center justify-center">
              <Loading
                className="!text-white !opacity-80"
                baseClassName="!text-primary"
              />
            </div>
          )}
          <div className="space-x-3 flex items-center h-full">
            {/* shrink-0 / whitespace-nowrap: keep the Back column and its
                contents together when the EN price column gets narrower. */}
            <ChevronLeftIcon
              className={classNames('shrink-0 fill-current text-primary', {
                '!text-white': isBacking,
              })}
            />
            <div
              className={classNames(
                'text-primary cursor-pointer whitespace-nowrap',
                {
                  '!text-white': isBacking,
                },
              )}
              onClick={() => {
                setIsBacking(true)
                onBack ? onBack() : router.back()
              }}
            >
              {t('Back')}
            </div>
          </div>
        </div>
        <div className="flex-1 lg:flex-none px-6 pr-0 lg:pr-6 lg:border-r border-gray-150 h-full flex flex-col justify-center space-y-1 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-gray font-lora text-xl font-bold">
              {hotelName}
            </h2>
            <div className="hidden lg:block">
              {isModifying ? (
                <div className="px-4">
                  <Loading
                    size="sm"
                    className="!text-white !opacity-60"
                    baseClassName="!text-primary"
                  />
                </div>
              ) : (
                <div
                  className="ml-4 text-primary text-sm cursor-pointer"
                  onClick={() => {
                    setIsModifying(true)
                    onModify && onModify()
                  }}
                >
                  {t('Modify')}
                </div>
              )}
            </div>
          </div>
          <div className="text-gray-light text-xs">{subTitle}</div>
          <div className="relative lg:hidden">
            {isModifying ? (
              <div className="relative text-left pt-4 pb-2 ml-2 flex justify-start">
                <Loading
                  size="sm"
                  className="!text-white !opacity-60"
                  baseClassName="!text-primary"
                />
              </div>
            ) : (
              <div
                className="mt-2 text-primary text-sm font-bold cursor-pointer"
                onClick={() => {
                  setIsModifying(true)
                  onModify && onModify()
                }}
              >
                {t('Modify')}
              </div>
            )}
          </div>
        </div>
        <div className="min-w-0 space-y-1 flex-1 flex flex-col justify-center items-end">
          <div className="text-xs text-gray space-x-1 text-right text-balance">
            {t('Total Costs')} ({t('Including Tax')})
          </div>
          <div
            className={classNames(
              'font-lora text-primary whitespace-nowrap',
              i18n.language === 'en' ? 'text-xl lg:text-2xl' : 'text-2xl',
            )}
            key={totalCost}
          >
            {formatToYen(totalCost)}
            {isUndated && '~'}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HotelSearchBreakcrumbs
